/**
 * Pengelola data: satu jendela untuk lapangan, sumur, marker, zona dan set kurva.
 *
 * Sebelum ini menu **Data** hanya berisi tiga baris yang membuka pesan "belum
 * tersedia", dan pekerjaan yang dijanjikannya sebenarnya tersebar: zona dan
 * marker hanya bisa disentuh dari layar korelasi — satu sumur pada satu waktu,
 * lewat menu klik-kanan di atas garisnya — sedangkan set kurva hanya dari pohon
 * proyek di layar Log, yang juga hanya menampilkan sumur yang sedang aktif.
 *
 * Yang hilang di antara keduanya adalah tatapan menyeluruh, dan justru itulah
 * satu-satunya tatapan yang bisa menjawab pertanyaan yang benar-benar
 * ditanyakan orang di depan lapangan berisi enam puluh sumur: sumur mana yang
 * belum punya Top Menggala, zona mana yang namanya salah ketik di tiga sumur,
 * berapa banyak set kurva lama yang masih menumpuk sejak sepuluh kali hitung
 * ulang. Ketiganya adalah pertanyaan tentang *lapangan*, bukan tentang sumur,
 * dan tidak satu pun bisa dijawab dengan membuka enam puluh sumur bergantian.
 *
 * Karena itu ketiga tab lintas-sumur di sini berbentuk sama: daftar nama di
 * kiri — marker, zona, atau set — dan di kanan seluruh sumur lapangan ini,
 * termasuk yang *tidak* memilikinya. Sumur yang kosong adalah setengah dari
 * jawabannya, dan daftar yang hanya memuat yang ada menyembunyikan persis
 * setengah itu.
 */

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import { api, unpage, type Page } from "../api";
import { canAdminister, canWrite, ROLE_LABELS } from "../auth";
import { useAsync } from "../hooks";
import { PALETTE } from "../lib/correlation";
import type { CurveSetBrief, Marker, Project, Well, Zone } from "../types";
import { ColorPicker } from "./ColorPicker";
import { DeleteDialog } from "./DeleteDialog";
import { Icon } from "./icons";
import { MarkerDialog } from "./MarkerTools";
import {
  Button,
  Dropdown,
  Empty,
  Loading,
  Modal,
  Notice,
  Segmented,
  TextField,
  useToast,
} from "./ui";

export type DataTab = "fields" | "wells" | "markers" | "zones" | "sets" | "logs";

const TABS: { value: DataTab; label: string }[] = [
  { value: "fields", label: "Fields" },
  { value: "wells", label: "Wells" },
  { value: "markers", label: "Marker" },
  { value: "zones", label: "Zones" },
  { value: "sets", label: "Curve sets" },
  // Berdiri di samping "Curve sets" dan bukan di dalamnya. Keduanya terlihat
  // berdekatan dan menjawab pertanyaan yang berbeda: di sana yang dipilih
  // adalah **versi** — hasil hitungan mana yang berlaku — sedangkan di sini
  // yang dinilai adalah **alatnya**, dan jawabannya sama untuk seluruh versi.
  { value: "logs", label: "Logs" },
];

const WELL_STATUS: { value: string; label: string }[] = [
  { value: "oil", label: "Oil well" },
  { value: "gas", label: "Gas well" },
  { value: "dry", label: "Dry / abandoned" },
  { value: "planned", label: "Planned" },
  { value: "suspended", label: "Suspended" },
];

/** Nama status sumur tanpa memanggil server; dipakai penyaring dan penyunting. */
const statusLabel = (value: string) =>
  WELL_STATUS.find((row) => row.value === value)?.label ?? value;

const num = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? "—" : value.toFixed(digits);

/* ------------------------------------------------------------ Primitif tabel */

interface Column<T> {
  key: string;
  label: string;
  /** Lebar tetap dalam piksel; kolom tanpa lebar membagi sisa ruangnya. */
  width?: number;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
}

const cellStyle = (align: "left" | "right" = "left"): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: align === "right" ? "flex-end" : "flex-start",
  minWidth: 0,
  padding: "0 8px",
  font: "400 11px var(--font-mono)",
  color: "var(--ink-2)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

/**
 * Kotak centang seukuran sel tabel.
 *
 * `Checkbox` di `ui` menempati satu baris penuh berikut labelnya — betul di
 * dalam panel, terlalu besar di dalam tabel yang barisnya setinggi 26 piksel.
 * Nama bacaannya tetap wajib: sederet kotak tanpa nama pada tabel enam puluh
 * baris tidak bisa dipakai sama sekali tanpa melihat layar.
 */
function Tick({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 14,
        height: 14,
        flex: "none",
        padding: 0,
        borderRadius: 3,
        border: `1px solid ${checked ? "var(--accent)" : "var(--border-control)"}`,
        background: checked ? "var(--accent)" : "var(--panel-inset)",
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {checked && (
        <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden>
          <path
            d="M1.5 5 L4 7.5 L8.5 2.5"
            stroke="var(--accent-contrast)"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

/**
 * Tabel yang barisnya bisa dipilih.
 *
 * Grid, bukan `<table>`: kepalanya harus menempel saat isinya digulung, dan
 * lima tabel di jendela ini semuanya berkolom lebar tetap kecuali satu yang
 * memakan sisanya. Keduanya pekerjaan sepuluh baris di grid dan pekerjaan
 * sepanjang hari di tabel.
 */
function Table<T extends { id: number }>({
  rows,
  columns,
  rowKey,
  selected,
  onSelected,
  selectLabel,
  onRowClick,
  activeId,
  empty,
}: {
  rows: T[];
  columns: Column<T>[];
  /** Kunci baris bila `id` tidak unik dalam tabel ini. */
  rowKey?: (row: T) => string | number;
  /** Ada berarti tabel ini bisa dipilih; kosong berarti hanya dibaca. */
  selected?: Set<number>;
  onSelected?: (next: Set<number>) => void;
  selectLabel?: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Baris yang sedang berlaku — sumur aktif, lapangan yang sedang dibuka. */
  activeId?: number | null;
  empty: ReactNode;
}) {
  const selectable = Boolean(selected && onSelected);
  const template = [
    selectable ? "26px" : null,
    ...columns.map((column) => (column.width ? `${column.width}px` : "minmax(90px, 1fr)")),
  ]
    .filter(Boolean)
    .join(" ");

  const allPicked = selectable && rows.length > 0 && rows.every((row) => selected!.has(row.id));

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: template,
          alignItems: "center",
          height: 24,
          flex: "none",
          background: "var(--panel-header)",
          borderBottom: "1px solid var(--line-strong)",
        }}
      >
        {selectable && (
          <div style={{ ...cellStyle(), padding: "0 6px" }}>
            <Tick
              checked={allPicked}
              label={allPicked ? "Clear the selection" : "Select every row"}
              disabled={rows.length === 0}
              onChange={(next) =>
                onSelected!(next ? new Set(rows.map((row) => row.id)) : new Set())
              }
            />
          </div>
        )}
        {columns.map((column) => (
          <div
            key={column.key}
            style={{
              ...cellStyle(column.align),
              font: "500 9.5px var(--font-mono)",
              color: "var(--ink-5)",
              letterSpacing: ".05em",
              textTransform: "uppercase",
            }}
          >
            {column.label}
          </div>
        ))}
      </div>

      <div className="scroll-y" style={{ flex: 1, minHeight: 0 }}>
        {rows.length === 0 && <Empty>{empty}</Empty>}
        {rows.map((row) => {
          const picked = selectable && selected!.has(row.id);
          const active = activeId !== undefined && activeId === row.id;
          return (
            <div
              key={rowKey ? rowKey(row) : row.id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                display: "grid",
                gridTemplateColumns: template,
                alignItems: "center",
                height: 26,
                background: picked
                  ? "var(--accent-wash)"
                  : active
                    ? "var(--selected)"
                    : "transparent",
                borderBottom: "1px solid var(--line-soft)",
                borderLeft: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                cursor: onRowClick ? "pointer" : "default",
              }}
              onMouseEnter={(event) => {
                if (!picked && !active) event.currentTarget.style.background = "var(--hover-soft)";
              }}
              onMouseLeave={(event) => {
                if (!picked && !active) event.currentTarget.style.background = "transparent";
              }}
            >
              {selectable && (
                <div style={{ ...cellStyle(), padding: "0 6px" }}>
                  <Tick
                    checked={picked}
                    label={selectLabel ? selectLabel(row) : `Select row ${row.id}`}
                    onChange={(next) => {
                      const copy = new Set(selected!);
                      next ? copy.add(row.id) : copy.delete(row.id);
                      onSelected!(copy);
                    }}
                  />
                </div>
              )}
              {columns.map((column) => (
                <div key={column.key} style={cellStyle(column.align)}>
                  {column.render(row)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Angka yang disunting di dalam sel, disimpan ketika kursornya pergi.
 *
 * Menyimpan pada tiap ketukan tombol berarti sebuah tulisan ke server untuk
 * "2", lalu "21", lalu "213" — dan sepersekian detik ketika kedalaman sebuah
 * top adalah 2 meter. Yang tersimpan adalah angka yang selesai diketik.
 */
function NumberCell({
  value,
  onCommit,
  disabled,
  label,
  digits = 1,
}: {
  value: number;
  onCommit: (value: number) => void;
  disabled?: boolean;
  label: string;
  digits?: number;
}) {
  const [text, setText] = useState(value.toFixed(digits));
  useEffect(() => setText(value.toFixed(digits)), [value, digits]);
  const commit = () => {
    const next = Number(text);
    if (!Number.isFinite(next) || next === value) {
      setText(value.toFixed(digits));
      return;
    }
    onCommit(next);
  };
  if (disabled) return <span>{value.toFixed(digits)}</span>;
  return (
    <input
      aria-label={label}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          setText(value.toFixed(digits));
          event.currentTarget.blur();
        }
      }}
      onClick={(event) => event.stopPropagation()}
      style={{
        width: "100%",
        minWidth: 0,
        height: 20,
        padding: "0 5px",
        textAlign: "right",
        background: "var(--panel-inset)",
        border: "1px solid var(--line-strong)",
        borderRadius: 3,
        font: "400 11px var(--font-mono)",
        color: "var(--ink)",
        outline: "none",
      }}
    />
  );
}

/** Bilah di atas tabel: pencarian, penyaring, lalu tombol yang berlaku. */
function Toolbar({
  query,
  onQuery,
  placeholder,
  children,
}: {
  query: string;
  onQuery: (value: string) => void;
  placeholder: string;
  children?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          height: 26,
          width: 210,
          padding: "0 8px",
          background: "var(--control)",
          border: "1px solid var(--border-control)",
          borderRadius: "var(--radius)",
        }}
      >
        {Icon.search({ size: 11, color: "var(--ink-6)" })}
        <input
          value={query}
          aria-label={placeholder}
          onChange={(event) => onQuery(event.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            font: "400 11px var(--font-sans)",
            color: "var(--ink)",
          }}
        />
      </div>
      {children}
    </div>
  );
}

/* --------------------------------------------------------------- Jendela */

export function DataManager({
  project,
  wells,
  activeWellId,
  tab,
  onTab,
  onWellSelect,
  onOpenProject,
  onChanged,
  onClose,
}: {
  project: Project;
  wells: Well[];
  activeWellId: number | null;
  tab: DataTab;
  onTab: (tab: DataTab) => void;
  onWellSelect: (wellId: number) => void;
  onOpenProject: (projectId: number) => void;
  /** Dipanggil sesudah sesuatu ditulis: daftar sumur dan layarnya dimuat ulang. */
  onChanged: () => void;
  onClose: () => void;
}) {
  const mayWrite = canWrite(project.my_role);
  const totals = useMemo(() => {
    let sets = 0;
    let curves = 0;
    let zones = 0;
    let markers = 0;
    for (const well of wells) {
      sets += well.curve_sets.length;
      curves += well.curve_sets.reduce((sum, set) => sum + set.curve_count, 0);
      zones += well.zones.length;
      markers += well.markers.length;
    }
    return { sets, curves, zones, markers };
  }, [wells]);

  return (
    <Modal title={`Data — ${project.name}`} width={1020} onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
        <Segmented value={tab} options={TABS} onChange={onTab} />
        <span style={{ flex: 1 }} />
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          {wells.length} sumur · {totals.sets} set · {totals.curves} kurva · {totals.zones} zona ·{" "}
          {totals.markers} marker
        </span>
      </div>

      {!mayWrite && tab !== "fields" && (
        <Notice tone="info">
          Your role in this field is read-only. Everything in it is open; what is closed
          are the buttons that write.
        </Notice>
      )}

      {/* Tinggi tetap, bukan tinggi isi: kelima tab ini berisi daftar yang
          panjangnya berbeda-beda, dan jendela yang tumbuh-menyusut setiap kali
          tab bergeser memindahkan tombolnya ke bawah telapak tangan. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 9, height: "min(64vh, 540px)", minHeight: 0 }}>
        {tab === "fields" && <FieldsTab project={project} onOpenProject={onOpenProject} />}
        {tab === "wells" && (
          <WellsTab
            project={project}
            wells={wells}
            activeWellId={activeWellId}
            mayWrite={mayWrite}
            onWellSelect={onWellSelect}
            onChanged={onChanged}
          />
        )}
        {tab === "markers" && (
          <MarkersTab project={project} wells={wells} mayWrite={mayWrite} onChanged={onChanged} />
        )}
        {tab === "zones" && (
          <ZonesTab project={project} wells={wells} mayWrite={mayWrite} onChanged={onChanged} />
        )}
        {tab === "sets" && (
          <SetsTab wells={wells} mayWrite={mayWrite} onChanged={onChanged} />
        )}
        {tab === "logs" && (
          <LogsTab project={project} mayWrite={mayWrite} onChanged={onChanged} />
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------- Lapangan */

/**
 * Seluruh lapangan yang boleh dilihat orang ini, bukan hanya yang sedang dibuka.
 *
 * Tidak ada penghapusan di sini, dan itu disengaja: sebuah lapangan dikeluarkan
 * dari lingkup kerja lewat saklar "aktif" — datanya tetap utuh dan tetap
 * terbaca — sebab menghapus lapangan adalah cara setahun interpretasi hilang
 * dalam satu klik. Baris daftar proyek yang menghapus tetap ada, satu per satu,
 * di layar daftar proyek tempat orang memang sedang mengurus proyek.
 */
function FieldsTab({
  project,
  onOpenProject,
}: {
  project: Project;
  onOpenProject: (projectId: number) => void;
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<"all" | "active" | "idle">("all");
  const [busy, setBusy] = useState<number | null>(null);

  const fields = useAsync(
    (signal) =>
      api
        .get<Page<Project>>("/api/projects/", undefined, signal)
        .then((page) => unpage(page, signal)),
    [],
  );

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (fields.data ?? []).filter((row) => {
      if (scope === "active" && !row.is_active) return false;
      if (scope === "idle" && row.is_active) return false;
      if (!needle) return true;
      return [row.name, row.code, row.basin, row.field].some((text) =>
        (text ?? "").toLowerCase().includes(needle),
      );
    });
  }, [fields.data, query, scope]);

  const flip = async (row: Project) => {
    setBusy(row.id);
    try {
      await api.post(`/api/projects/${row.id}/set_active/`, { is_active: !row.is_active });
      toast(`${row.name} ${row.is_active ? "taken out of" : "brought back into"} the working scope.`);
      fields.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(null);
    }
  };

  if (fields.loading) return <Loading label="Loading the field list…" />;
  if (fields.error) return <Notice tone="error">{fields.error}</Notice>;

  const columns: Column<Project>[] = [
    {
      key: "name",
      label: "Fields",
      render: (row) => (
        <span style={{ display: "flex", alignItems: "baseline", gap: 7, minWidth: 0 }}>
          <span
            style={{
              font: "600 11.5px var(--font-sans)",
              color: row.is_active ? "var(--ink)" : "var(--ink-5)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.name}
          </span>
          <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-7)" }}>{row.code}</span>
        </span>
      ),
    },
    { key: "basin", label: "Cekungan", width: 130, render: (row) => row.basin || "—" },
    { key: "crs", label: "CRS", width: 150, render: (row) => row.crs },
    { key: "wells", label: "Wells", width: 62, align: "right", render: (row) => row.well_count },
    { key: "curves", label: "Curves", width: 62, align: "right", render: (row) => row.curve_count },
    { key: "members", label: "Members", width: 68, align: "right", render: (row) => row.member_count },
    {
      key: "role",
      label: "Peran",
      width: 110,
      render: (row) => (row.my_role ? ROLE_LABELS[row.my_role] : "—"),
    },
    {
      key: "active",
      label: "Lingkup",
      width: 120,
      render: (row) =>
        canAdminister(row.my_role) ? (
          <Button
            variant="ghost"
            busy={busy === row.id}
            onClick={() => flip(row)}
            style={{ height: 20, font: "500 10px var(--font-sans)" }}
            title={
              row.is_active
                ? "Take this field out of the working scope; its data stays whole"
                : "Bring this field back into the working scope"
            }
          >
            {row.is_active ? "Active" : "Disimpan"}
          </Button>
        ) : (
          <span style={{ color: row.is_active ? "var(--ink-2)" : "var(--ink-6)" }}>
            {row.is_active ? "Active" : "Disimpan"}
          </span>
        ),
    },
  ];

  return (
    <>
      <Toolbar query={query} onQuery={setQuery} placeholder="Search field, code, basin…">
        <Segmented
          value={scope}
          size="sm"
          options={[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "idle", label: "Disimpan" },
          ]}
          onChange={setScope}
        />
        <span style={{ flex: 1 }} />
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          click a row to open it
        </span>
      </Toolbar>
      <Table
        rows={rows}
        columns={columns}
        activeId={project.id}
        onRowClick={(row) => onOpenProject(row.id)}
        empty="No field matches."
      />
    </>
  );
}

/* ------------------------------------------------------------------ Sumur */

function WellsTab({
  project,
  wells,
  activeWellId,
  mayWrite,
  onWellSelect,
  onChanged,
}: {
  project: Project;
  wells: Well[];
  activeWellId: number | null;
  mayWrite: boolean;
  onWellSelect: (wellId: number) => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<Well | "new" | null>(null);
  const [doomed, setDoomed] = useState<Well[] | null>(null);
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return wells.filter((well) => {
      if (status !== "all" && well.status !== status) return false;
      if (!needle) return true;
      return [well.name, well.uwi, well.operator].some((text) =>
        (text ?? "").toLowerCase().includes(needle),
      );
    });
  }, [wells, query, status]);

  const chosen = wells.filter((well) => picked.has(well.id));

  const remove = async () => {
    if (!doomed) return;
    setBusy(true);
    try {
      await api.post("/api/wells/bulk-delete/", { ids: doomed.map((well) => well.id) });
      toast(
        doomed.length === 1
          ? `Well ${doomed[0].name} deleted.`
          : `${doomed.length} wells deleted.`,
      );
      setDoomed(null);
      setPicked(new Set());
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<Well>[] = [
    {
      key: "name",
      label: "Well",
      render: (well) => (
        <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
          <span
            style={{
              width: 7,
              height: 7,
              flex: "none",
              borderRadius: 2,
              background: well.panel_color,
              border: well.status === "planned" ? "1px solid var(--ink-6)" : "none",
            }}
          />
          <span
            style={{
              font: "500 11.5px var(--font-sans)",
              color: "var(--ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {well.name}
          </span>
        </span>
      ),
    },
    { key: "uwi", label: "UWI", width: 118, render: (well) => well.uwi || "—" },
    { key: "status", label: "Status", width: 128, render: (well) => statusLabel(well.status) },
    { key: "operator", label: "Operator", width: 120, render: (well) => well.operator || "—" },
    { key: "kb", label: "RT", width: 60, align: "right", render: (well) => num(well.kb_elevation) },
    { key: "td", label: "TD", width: 74, align: "right", render: (well) => num(well.td_md) },
    {
      key: "sets",
      label: "Set",
      width: 50,
      align: "right",
      render: (well) => well.curve_sets.length,
    },
    {
      key: "curves",
      label: "Curves",
      width: 56,
      align: "right",
      render: (well) => well.curve_sets.reduce((sum, set) => sum + set.curve_count, 0),
    },
    { key: "zones", label: "Zones", width: 52, align: "right", render: (well) => well.zones.length },
    {
      key: "markers",
      label: "Markers",
      width: 60,
      align: "right",
      render: (well) => well.markers.length,
    },
    {
      key: "edit",
      label: "",
      width: 56,
      render: (well) =>
        mayWrite ? (
          <Button
            variant="ghost"
            onClick={() => setEditing(well)}
            title={`Edit the details of well ${well.name}`}
            style={{ height: 20, font: "500 10px var(--font-sans)" }}
          >
            Ubah
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Toolbar query={query} onQuery={setQuery} placeholder="Search well, UWI, operator…">
        <Dropdown
          value={status}
          compact
          width={132}
          options={[{ value: "all", label: "Every status" }, ...WELL_STATUS]}
          onChange={setStatus}
        />
        <span style={{ flex: 1 }} />
        {picked.size > 0 && (
          <>
            <span style={{ font: "400 10px var(--font-mono)", color: "var(--accent)" }}>
              {picked.size} terpilih
            </span>
            <Button
              variant="danger"
              disabled={!mayWrite}
              onClick={() => setDoomed(chosen)}
              style={{ height: 26 }}
            >
              {Icon.trash({ size: 11 })} Hapus
            </Button>
          </>
        )}
        {mayWrite && (
          <Button variant="primary" onClick={() => setEditing("new")} style={{ height: 26 }}>
            {Icon.plus({ size: 11 })} Sumur baru
          </Button>
        )}
      </Toolbar>

      <Table
        rows={rows}
        columns={columns}
        selected={picked}
        onSelected={mayWrite ? setPicked : undefined}
        selectLabel={(well) => `Select well ${well.name}`}
        activeId={activeWellId}
        onRowClick={(well) => onWellSelect(well.id)}
        empty={
          wells.length === 0
            ? "This field has no well yet. Import a LAS, or create a planned well."
            : "No well matches."
        }
      />

      {editing && (
        <WellDialog
          project={project}
          well={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            toast(message);
            setEditing(null);
            onChanged();
          }}
        />
      )}

      {doomed && (
        <DeleteDialog
          title={doomed.length === 1 ? "Delete well" : `Delete ${doomed.length} wells`}
          what={doomed.length === 1 ? `Well ${doomed[0].name}` : doomed.map((w) => w.name).join(", ")}
          consequences={[
            `${doomed.reduce((n, w) => n + w.curve_sets.length, 0)} curve sets`,
            `${doomed.reduce((n, w) => n + w.curve_sets.reduce((s, set) => s + set.curve_count, 0), 0)} curves`,
            `${doomed.reduce((n, w) => n + w.zones.length, 0)} zones`,
            `${doomed.reduce((n, w) => n + w.markers.length, 0)} markers`,
            "every computation result it carries",
          ]}
          /* Satu sumur diketik namanya, sebagaimana di pohon proyek. Untuk
             banyak sumur tidak ada satu nama yang bisa diketik, dan menuntut
             sepuluh nama berturut-turut hanya melatih orang menyalin-tempel;
             yang diminta adalah satu kata yang tidak mungkin terketik
             tanpa sengaja. */
          confirmWord={doomed.length === 1 ? doomed[0].name : "DELETE"}
          busy={busy}
          onCancel={() => setDoomed(null)}
          onConfirm={remove}
        />
      )}
    </>
  );
}

/** Membuat sumur baru, atau mengubah keterangan sumur yang sudah ada. */
function WellDialog({
  project,
  well,
  onClose,
  onSaved,
}: {
  project: Project;
  well: Well | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(well?.name ?? "");
  const [uwi, setUwi] = useState(well?.uwi ?? "");
  const [status, setStatus] = useState(well?.status ?? "oil");
  const [operator, setOperator] = useState(well?.operator ?? "");
  const [surfaceX, setSurfaceX] = useState(well?.surface_x?.toString() ?? "");
  const [surfaceY, setSurfaceY] = useState(well?.surface_y?.toString() ?? "");
  const [kb, setKb] = useState(well?.kb_elevation?.toString() ?? "");
  const [gl, setGl] = useState(well?.gl_elevation?.toString() ?? "");
  const [td, setTd] = useState(well?.td_md?.toString() ?? "");
  const [color, setColor] = useState(well?.display_color ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Kotak kosong berarti "tidak diketahui", bukan nol. */
  const optional = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
  };

  const save = async () => {
    if (!name.trim()) {
      setError("A well must have a name.");
      return;
    }
    setBusy(true);
    setError(null);
    const body = {
      project: project.id,
      name: name.trim(),
      uwi: uwi.trim(),
      status,
      operator: operator.trim(),
      surface_x: optional(surfaceX),
      surface_y: optional(surfaceY),
      kb_elevation: optional(kb),
      gl_elevation: optional(gl),
      td_md: optional(td),
      display_color: color,
    };
    try {
      if (well) {
        await api.patch(`/api/wells/${well.id}/`, body);
        onSaved(`Well ${body.name} updated.`);
      } else {
        await api.post("/api/wells/", body);
        onSaved(`Well ${body.name} created.`);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={well ? `Edit well — ${well.name}` : "New well"}
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={save}>
            {well ? "Simpan" : "Buat"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <TextField label="NAME" value={name} onChange={setName} autoFocus={!well} />
        <TextField label="UWI" value={uwi} onChange={setUwi} placeholder="opsional" />
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
        <Dropdown label="STATUS" value={status} options={WELL_STATUS} onChange={setStatus} width={168} />
        <div style={{ flex: 1 }}>
          <TextField label="OPERATOR" value={operator} onChange={setOperator} placeholder="opsional" />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
        <TextField label="X" value={surfaceX} onChange={setSurfaceX} placeholder="—" />
        <TextField label="Y" value={surfaceY} onChange={setSurfaceY} placeholder="—" />
        <TextField label="RT (M)" value={kb} onChange={setKb} placeholder="—" />
        <TextField label="TD MD (M)" value={td} onChange={setTd} placeholder="—" />
      </div>
      {/* Elevasi tanah tampak seperti kolom pelengkap, dan bukan: ia yang
          memisahkan TVD dari TVDRT. Dibiarkan kosong, sumbu TVD terpaksa jatuh
          kembali ke meja putar dan tiap kedalaman terbaca setinggi lantai bor
          terlalu dalam. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10 }}>
        <TextField label="GROUND (M)" value={gl} onChange={setGl} placeholder="—" />
        <div style={{ alignSelf: "end", font: "11px var(--font-sans)", color: "var(--ink-4)", paddingBottom: 6 }}>
          Ground level elevation — the TVD datum. Left blank, TVD falls back to the rotary table.
        </div>
      </div>
      <ColorPicker
        value={color || (well?.symbol_color ?? "#8b939e")}
        onChange={setColor}
        label="Panel colour"
      />
      {/* Warna kosong berarti warna status, dan itu perlu bisa dikembalikan:
          sekali sebuah warna dipilih, tidak ada jalan lain menuju bawaannya. */}
      {color && (
        <Button variant="ghost" onClick={() => setColor("")} style={{ height: 24, alignSelf: "flex-start" }}>
          Back to the status colour
        </Button>
      )}
      {!well && (
        <Notice tone="info">
          Sumur baru lahir tanpa kurva. Sumur rencana memang begitu; sumur yang sudah dibor diisi
          lewat Import LAS.
        </Notice>
      )}
      {error && <Notice tone="error">{error}</Notice>}
    </Modal>
  );
}

/* ------------------------------------ Kerangka lintas-sumur: daftar nama + rincian */

interface NameRow {
  key: string;
  label: string;
  color?: string;
  meta: string;
}

/**
 * Daftar nama di kiri: marker, zona, atau set kurva sebuah lapangan.
 *
 * Yang dihitung di kolom kanannya adalah "berapa sumur dari berapa", bukan
 * jumlah barisnya saja. 12/61 dan 61/61 adalah dua keadaan yang sama sekali
 * berbeda bagi sebuah top, dan angka tunggal "12" tidak membedakan keduanya.
 */
function NameList({
  rows,
  selected,
  onSelect,
  query,
  onQuery,
  placeholder,
  action,
  empty,
}: {
  rows: NameRow[];
  selected: string | null;
  onSelect: (key: string) => void;
  query: string;
  onQuery: (value: string) => void;
  placeholder: string;
  action?: ReactNode;
  empty: string;
}) {
  const needle = query.trim().toLowerCase();
  const shown = needle ? rows.filter((row) => row.label.toLowerCase().includes(needle)) : rows;
  return (
    <div
      style={{
        width: 244,
        flex: "none",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        border: "1px solid var(--border-control)",
        borderRadius: "var(--radius-panel)",
        overflow: "hidden",
        background: "var(--panel-inset)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 7px",
          borderBottom: "1px solid var(--line-soft)",
          flex: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: 1,
            minWidth: 0,
            height: 24,
            padding: "0 7px",
            background: "var(--control)",
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius)",
          }}
        >
          {Icon.search({ size: 10, color: "var(--ink-6)" })}
          <input
            value={query}
            aria-label={placeholder}
            placeholder={placeholder}
            onChange={(event) => onQuery(event.target.value)}
            style={{
              flex: 1,
              minWidth: 0,
              background: "transparent",
              border: "none",
              outline: "none",
              font: "400 10.5px var(--font-sans)",
              color: "var(--ink)",
            }}
          />
        </div>
        {action}
      </div>
      <div className="scroll-y" style={{ flex: 1, minHeight: 0, padding: "3px 0" }}>
        {shown.length === 0 && (
          <div style={{ padding: "14px 10px", font: "400 10.5px var(--font-sans)", color: "var(--ink-6)" }}>
            {empty}
          </div>
        )}
        {shown.map((row) => {
          const active = row.key === selected;
          return (
            <button
              key={row.key}
              type="button"
              onClick={() => onSelect(row.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                width: "100%",
                height: 26,
                padding: "0 9px",
                border: "none",
                borderLeft: `2px solid ${active ? "var(--accent)" : "transparent"}`,
                background: active ? "var(--selected)" : "transparent",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {row.color && (
                <span
                  style={{ width: 7, height: 7, flex: "none", borderRadius: 2, background: row.color }}
                />
              )}
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  font: `${active ? 600 : 500} 11px var(--font-sans)`,
                  color: active ? "var(--ink)" : "var(--ink-3)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.label}
              </span>
              <span style={{ font: "400 9.5px var(--font-mono)", color: active ? "var(--accent)" : "var(--ink-7)" }}>
                {row.meta}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Rincian di kanan: bilah tombol di atas, tabel sumur di bawahnya. */
function Detail({
  bar,
  filter,
  children,
}: {
  bar: ReactNode;
  /** Baris penyaring di bawah bilah tombol; kosong berarti tabelnya utuh. */
  filter?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        border: "1px solid var(--border-control)",
        borderRadius: "var(--radius-panel)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 8px",
          borderBottom: "1px solid var(--line-soft)",
          flex: "none",
          flexWrap: "wrap",
        }}
      >
        {bar}
      </div>
      {filter && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 8px",
            borderBottom: "1px solid var(--line-soft)",
            flex: "none",
            background: "var(--panel-inset)",
          }}
        >
          {filter}
        </div>
      )}
      {children}
    </div>
  );
}

/** Cakupan tabel sumur: seluruhnya, yang punya saja, atau yang belum saja. */
type Scope = "all" | "has" | "missing";

/**
 * Penyaring tabel sumur — cakupan di kiri, nama di kanan.
 *
 * Tabel yang memuat seluruh sumur lapangan adalah yang benar bagi enam sumur
 * dan tidak terpakai bagi seribu empat ratus: sebuah top yang terpasang pada
 * satu sumur saja tenggelam di antara 1.401 baris "—", dan menemukannya
 * berarti menggulir sampai ketemu. Ketiga cakupan ini adalah tiga pertanyaan
 * yang memang ditanyakan orang — "mana saja yang sudah", "mana yang belum",
 * "perlihatkan semuanya" — dan jumlahnya tertulis di tombolnya sendiri supaya
 * jawabannya terbaca sebelum ada yang ditekan.
 */
function RowFilter({
  scope,
  onScope,
  query,
  onQuery,
  counts,
}: {
  scope: Scope;
  onScope: (scope: Scope) => void;
  query: string;
  onQuery: (value: string) => void;
  /** Ketiganya menghitung baris tabel, bukan sumur: satu sumur bisa punya banyak versi set. */
  counts: { all: number; has: number; missing: number };
}) {
  return (
    <>
      <Segmented
        size="sm"
        value={scope}
        options={[
          { value: "all" as Scope, label: `All ${counts.all}` },
          { value: "has" as Scope, label: `Has ${counts.has}` },
          { value: "missing" as Scope, label: `Without ${counts.missing}` },
        ]}
        onChange={onScope}
      />
      <span style={{ flex: 1 }} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          width: 190,
          height: 22,
          padding: "0 7px",
          background: "var(--control)",
          border: "1px solid var(--border-control)",
          borderRadius: "var(--radius)",
        }}
      >
        {Icon.search({ size: 10, color: "var(--ink-6)" })}
        <input
          value={query}
          aria-label="Search wells in this list"
          placeholder="Search wells…"
          onChange={(event) => onQuery(event.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            font: "400 10.5px var(--font-sans)",
            color: "var(--ink)",
          }}
        />
      </div>
    </>
  );
}

/** Menyaring baris sumur menurut cakupan dan nama; dipakai ketiga tab lintas-sumur. */
function narrow<T extends { well: Well }>(
  rows: T[],
  has: (row: T) => boolean,
  scope: Scope,
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (scope === "has" && !has(row)) return false;
    if (scope === "missing" && has(row)) return false;
    return !needle || row.well.name.toLowerCase().includes(needle);
  });
}

/** Menulis satu perubahan ke banyak baris; memulangkan berapa yang gagal. */
async function patchEach(paths: string[], body: unknown): Promise<number> {
  const results = await Promise.allSettled(paths.map((path) => api.patch(path, body)));
  return results.filter((result) => result.status === "rejected").length;
}

/**
 * Daftar sumur berkotak centang, dipakai kedua dialog "pasang di sumur".
 *
 * Yang sudah punya tetap tercantum dan tetap bisa dipilih — memasang ulang di
 * atasnya adalah cara membetulkan sepuluh kedalaman yang salah sekaligus —
 * tetapi ditandai, supaya tidak ada yang menimpanya tanpa tahu.
 */
function WellPicker({
  wells,
  has,
  picked,
  onPicked,
}: {
  wells: Well[];
  has: (well: Well) => boolean;
  picked: Set<number>;
  onPicked: (next: Set<number>) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)", letterSpacing: ".04em" }}>
          SUMUR — {picked.size} dari {wells.length}
        </span>
        <span style={{ flex: 1 }} />
        <Button
          variant="ghost"
          onClick={() => onPicked(new Set(wells.map((well) => well.id)))}
          style={{ height: 20, font: "500 10px var(--font-sans)" }}
        >
          All
        </Button>
        <Button
          variant="ghost"
          onClick={() => onPicked(new Set(wells.filter((well) => !has(well)).map((well) => well.id)))}
          style={{ height: 20, font: "500 10px var(--font-sans)" }}
        >
          The ones without
        </Button>
      </div>
      <div
        className="scroll-y"
        style={{
          maxHeight: 168,
          padding: 4,
          background: "var(--panel-inset)",
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--radius)",
        }}
      >
        {wells.map((well) => (
          <div
            key={well.id}
            style={{ display: "flex", alignItems: "center", gap: 8, height: 22, padding: "0 4px" }}
          >
            <Tick
              checked={picked.has(well.id)}
              label={`Select well ${well.name}`}
              onChange={(next) => {
                const copy = new Set(picked);
                next ? copy.add(well.id) : copy.delete(well.id);
                onPicked(copy);
              }}
            />
            <span style={{ flex: 1, font: "400 11px var(--font-sans)", color: "var(--ink-2)" }}>
              {well.name}
            </span>
            <span style={{ font: "400 9.5px var(--font-mono)", color: has(well) ? "var(--amber)" : "var(--ink-7)" }}>
              {has(well) ? "has it" : "has none"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mengganti nama sesuatu di seluruh sumur sebuah lapangan. */
function RenameDialog({
  title,
  what,
  initial,
  note,
  busy,
  onSubmit,
  onClose,
}: {
  title: string;
  what: string;
  initial: string;
  note?: ReactNode;
  busy: boolean;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial);
  return (
    <Modal
      title={title}
      width={430}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={!name.trim() || name.trim() === initial}
            onClick={() => onSubmit(name.trim())}
          >
            Rename
          </Button>
        </>
      }
    >
      <span style={{ font: "400 12px var(--font-sans)", color: "var(--ink-2)", lineHeight: 1.6 }}>
        {what}
      </span>
      <TextField label="NEW NAME" value={name} onChange={setName} autoFocus />
      {note}
    </Modal>
  );
}

/* ----------------------------------------------------------------- Marker */

const CONFIDENCE_LABEL: Record<string, string> = {
  high: "High",
  medium: "Sedang",
  low: "Low",
};

const SOURCE_LABEL: Record<string, string> = {
  log: "Log",
  core: "Log + core",
  seismic: "Seismik",
};

interface MarkerGroup {
  name: string;
  color: string;
  rows: { marker: Marker; well: Well }[];
  /** Kedalaman rata-rata; hanya dipakai untuk mengurutkan daftar namanya. */
  mean: number;
}

/**
 * Marker seluruh lapangan, dikelompokkan menurut namanya.
 *
 * Urutan daftar namanya adalah urutan kedalaman rata-ratanya, bukan abjad.
 * Sebuah daftar top yang berbunyi "Base Sand, Top Menggala, Top Sihapas" tidak
 * mengatakan apa-apa tentang batuannya; urutan kedalaman memulangkan kolom
 * stratigrafi lapangan itu sebagaimana orang membacanya.
 */
function MarkersTab({
  project,
  wells,
  mayWrite,
  onChanged,
}: {
  project: Project;
  wells: Well[];
  mayWrite: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [scope, setScope] = useState<Scope>("all");
  const [wellQuery, setWellQuery] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [applying, setApplying] = useState<{ name: string } | null>(null);
  const [editing, setEditing] = useState<{ well: Well; marker: Marker } | null>(null);
  const [doomed, setDoomed] = useState<{ what: string; ids: number[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const groups = useMemo<MarkerGroup[]>(() => {
    const map = new Map<string, MarkerGroup>();
    for (const well of wells)
      for (const marker of well.markers) {
        const group =
          map.get(marker.name) ?? { name: marker.name, color: marker.color, rows: [], mean: 0 };
        group.rows.push({ marker, well });
        map.set(marker.name, group);
      }
    const list = [...map.values()];
    for (const group of list)
      group.mean = group.rows.reduce((sum, row) => sum + row.marker.md, 0) / group.rows.length;
    return list.sort((a, b) => a.mean - b.mean);
  }, [wells]);

  const group = groups.find((row) => row.name === chosen) ?? groups[0] ?? null;

  const rows = useMemo(
    () =>
      group
        ? wells.map((well) => ({
            id: well.id,
            well,
            marker: well.markers.find((marker) => marker.name === group.name) ?? null,
          }))
        : [],
    [wells, group],
  );

  const shown = useMemo(
    () => narrow(rows, (row) => row.marker !== null, scope, wellQuery),
    [rows, scope, wellQuery],
  );

  const pickedRows = shown.filter((row) => picked.has(row.id));
  const write = async (label: string, work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      toast(label);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: "well",
      label: "Well",
      render: (row) => (
        <span style={{ font: "500 11px var(--font-sans)", color: row.marker ? "var(--ink)" : "var(--ink-6)" }}>
          {row.well.name}
        </span>
      ),
    },
    {
      key: "md",
      label: "MD (m)",
      width: 92,
      align: "right",
      render: (row) =>
        row.marker ? (
          <NumberCell
            value={row.marker.md}
            digits={2}
            disabled={!mayWrite}
            label={`Depth of ${row.marker.name} on ${row.well.name}`}
            onCommit={(md) =>
              write(`${row.marker!.name} on ${row.well.name} moved to ${md.toFixed(2)} m.`, () =>
                api.patch(`/api/markers/${row.marker!.id}/`, { md }),
              )
            }
          />
        ) : (
          <span style={{ color: "var(--ink-7)" }}>—</span>
        ),
    },
    {
      key: "confidence",
      label: "Confidence",
      width: 92,
      render: (row) =>
        row.marker ? (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: row.marker.confidence_color,
              }}
            />
            {CONFIDENCE_LABEL[row.marker.confidence] ?? row.marker.confidence}
          </span>
        ) : null,
    },
    {
      key: "source",
      label: "Sumber",
      width: 90,
      render: (row) => (row.marker ? (SOURCE_LABEL[row.marker.source] ?? row.marker.source) : null),
    },
    {
      key: "act",
      label: "",
      width: 62,
      render: (row) =>
        row.marker && mayWrite ? (
          <Button
            variant="ghost"
            onClick={() => setEditing({ well: row.well, marker: row.marker! })}
            title={`Edit ${row.marker.name} on ${row.well.name}`}
            style={{ height: 20, font: "500 10px var(--font-sans)" }}
          >
            Ubah
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: "flex", gap: 9, flex: 1, minHeight: 0 }}>
      <NameList
        rows={groups.map((row) => ({
          key: row.name,
          label: row.name,
          color: row.color,
          meta: `${row.rows.length}/${wells.length}`,
        }))}
        selected={group?.name ?? null}
        onSelect={(key) => {
          setChosen(key);
          setPicked(new Set());
        }}
        query={query}
        onQuery={setQuery}
        placeholder="Search top name…"
        empty="This field has no marker yet."
        action={
          mayWrite ? (
            <Button
              variant="ghost"
              title="Set one new top on many wells at once"
              onClick={() => setApplying({ name: "" })}
              style={{ height: 24, font: "500 10px var(--font-sans)" }}
            >
              {Icon.plus({ size: 10 })} Baru
            </Button>
          ) : undefined
        }
      />

      {group === null ? (
        <Detail bar={<span className="label">Markers</span>}>
          <Empty>
            No marker in this field yet. Set the first one with the New button, or by right-
            clicking on the log sheet.
          </Empty>
        </Detail>
      ) : (
        <Detail
          bar={
            <>
              <span style={{ font: "600 11.5px var(--font-sans)", color: "var(--ink)" }}>
                {group.name}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {group.rows.length} dari {wells.length} sumur ·{" "}
                {Math.min(...group.rows.map((row) => row.marker.md)).toFixed(0)}–
                {Math.max(...group.rows.map((row) => row.marker.md)).toFixed(0)} m
              </span>
              <span style={{ flex: 1 }} />
              {mayWrite && (
                <>
                  {/* Warnanya milik namanya, bukan milik satu sumur: garis tali
                      di lembar korelasi menyambung nama yang sama, dan dua
                      warna untuk satu nama membuat garis itu berganti rupa di
                      tengah lembar. */}
                  <ColorPicker
                    value={group.color}
                    compact
                    label={`Colour of ${group.name}`}
                    onChange={(color) =>
                      write(`Colour of ${group.name} changed on ${group.rows.length} wells.`, async () => {
                        const failed = await patchEach(
                          group.rows.map((row) => `/api/markers/${row.marker.id}/`),
                          { color },
                        );
                        if (failed) throw new Error(`${failed} sumur gagal diubah.`);
                      })
                    }
                  />
                  <Button variant="ghost" onClick={() => setRenaming(true)} style={{ height: 22 }}>
                    Rename
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setApplying({ name: group.name })}
                    style={{ height: 22 }}
                  >
                    Set on wells…
                  </Button>
                  <Button
                    variant="danger"
                    disabled={group.rows.length === 0}
                    style={{ height: 22 }}
                    onClick={() => {
                      // Tanpa centang, yang terhapus adalah apa yang sedang
                      // terlihat — bukan seluruh lapangan. Penyaring yang
                      // menyempitkan tabel tetapi tidak menyempitkan tombol
                      // hapusnya adalah jebakan.
                      const targets = (pickedRows.length ? pickedRows : shown).filter(
                        (row) => row.marker,
                      );
                      if (!targets.length) {
                        toast("No marker on the wells that were picked.", "error");
                        return;
                      }
                      setDoomed({
                        what:
                          targets.length === group.rows.length
                            ? `${group.name} on all ${targets.length} wells`
                            : `${group.name} on ${targets.length} picked wells`,
                        ids: targets.map((row) => row.marker!.id),
                      });
                    }}
                  >
                    {Icon.trash({ size: 10 })} Hapus
                  </Button>
                </>
              )}
            </>
          }
          filter={
            <RowFilter
              scope={scope}
              onScope={setScope}
              query={wellQuery}
              onQuery={setWellQuery}
              counts={{
                all: wells.length,
                has: group.rows.length,
                missing: wells.length - group.rows.length,
              }}
            />
          }
        >
          <Table
            rows={shown}
            columns={columns}
            selected={picked}
            onSelected={mayWrite ? setPicked : undefined}
            selectLabel={(row) => `Select well ${row.well.name}`}
            empty={
              wells.length === 0
                ? "This field has no well yet."
                : "No well matches this filter."
            }
          />
        </Detail>
      )}

      {renaming && group && (
        <RenameDialog
          title="Rename marker"
          what={`${group.name} is set on ${group.rows.length} wells. The name changes on all of them at once.`}
          initial={group.name}
          busy={busy}
          note={
            <Notice tone="info">
              A well that already carries a marker under the target name is skipped, not
              overwritten — two depths for one name cannot be joined without picking one of
              them, and that choice does not belong to a button.
            </Notice>
          }
          onClose={() => setRenaming(false)}
          onSubmit={(name) =>
            write(`${group.name} renamed to ${name}.`, async () => {
              const result = await api.post<{ renamed: number; clashed: string[] }>(
                "/api/markers/rename/",
                { project: project.id, from: group.name, to: name },
              );
              setRenaming(false);
              setChosen(name);
              if (result.clashed?.length)
                toast(`Skipped because they already carry ${name}: ${result.clashed.join(", ")}`, "error");
            })
          }
        />
      )}

      {applying && (
        <MarkerApplyDialog
          project={project}
          wells={wells}
          name={applying.name}
          preset={picked}
          onClose={() => setApplying(null)}
          onDone={(name, message) => {
            setApplying(null);
            setChosen(name);
            toast(message);
            onChanged();
          }}
        />
      )}

      {editing && (
        <MarkerDialog
          projectId={project.id}
          wellId={editing.well.id}
          wellName={editing.well.name}
          draft={{
            id: editing.marker.id,
            name: editing.marker.name,
            md: editing.marker.md,
            color: editing.marker.color,
            confidence: editing.marker.confidence,
            source: editing.marker.source,
          }}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            toast(message);
            onChanged();
          }}
        />
      )}

      {doomed && (
        <DeleteDialog
          title="Delete marker"
          what={doomed.what}
          consequences={
            doomed.ids.length > 1
              ? ["the correlation line on the sheet that ties this name together"]
              : []
          }
          confirmWord={doomed.ids.length > 4 ? "DELETE" : undefined}
          busy={busy}
          onCancel={() => setDoomed(null)}
          onConfirm={() =>
            write(`${doomed.ids.length} markers deleted.`, async () => {
              await api.post("/api/markers/bulk-delete/", { ids: doomed.ids });
              setDoomed(null);
              setPicked(new Set());
            })
          }
        />
      )}
    </div>
  );
}

/** Memasang satu top bernama sama di banyak sumur sekaligus. */
function MarkerApplyDialog({
  project,
  wells,
  name: initial,
  preset,
  onClose,
  onDone,
}: {
  project: Project;
  wells: Well[];
  name: string;
  /** Sumur yang sudah tercentang di tabel rincian, bila ada. */
  preset: Set<number>;
  onClose: () => void;
  onDone: (name: string, message: string) => void;
}) {
  const [name, setName] = useState(initial);
  const has = (well: Well) => well.markers.some((marker) => marker.name === name);
  const existing = useMemo(
    () => wells.flatMap((well) => well.markers.filter((marker) => marker.name === initial)),
    [wells, initial],
  );
  /* Kedalaman bawaannya adalah median yang sudah terpasang, bukan nol dan
     bukan angka bulat yang enak dilihat: sumur yang belum punya top ini hampir
     selalu berada di antara sumur yang sudah punya. */
  const median = existing.length
    ? [...existing].sort((a, b) => a.md - b.md)[Math.floor(existing.length / 2)].md
    : 0;
  const [md, setMd] = useState(median ? median.toFixed(1) : "");
  const [color, setColor] = useState(existing[0]?.color ?? PALETTE[0]);
  const [picked, setPicked] = useState<Set<number>>(() =>
    preset.size
      ? new Set(preset)
      : new Set(wells.filter((well) => !well.markers.some((m) => m.name === initial)).map((w) => w.id)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const depth = Number(md);
    if (!name.trim()) {
      setError("A marker must have a name.");
      return;
    }
    if (!Number.isFinite(depth)) {
      setError("Give the default depth, in metres MD.");
      return;
    }
    if (picked.size === 0) {
      setError("Pick at least one well.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ created: number[]; existing: number[] }>(
        "/api/markers/apply-to-wells/",
        { project: project.id, name: name.trim(), md: depth, color, wells: [...picked] },
      );
      onDone(
        name.trim(),
        `${name.trim()}: ${result.created.length} set, ${result.existing.length} already there and left alone.`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={initial ? `Set ${initial} on other wells` : "New marker on many wells"}
      width={470}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={submit}>
            Pasang
          </Button>
        </>
      }
    >
      <TextField label="TOP NAME" value={name} onChange={setName} autoFocus={!initial} />
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <TextField label="DEFAULT DEPTH (M MD)" value={md} onChange={setMd} />
        </div>
        <ColorPicker value={color} onChange={setColor} label="Colour" />
      </div>
      <Notice tone="info">
        A well that already carries this top is left untouched — its depth there is somebody's
        reading, and the default depth above is only a starting point for wells that have none.
        The ones newly set are marked <b>low</b> confidence, because they are a prediction until
        somebody checks them on the log screen.
      </Notice>
      <WellPicker wells={wells} has={has} picked={picked} onPicked={setPicked} />
      {error && <Notice tone="error">{error}</Notice>}
    </Modal>
  );
}

/* ------------------------------------------------------------------- Zona */

interface ZoneGroup {
  name: string;
  color: string;
  rows: { zone: Zone; well: Well }[];
  mean: number;
}

/**
 * Zona seluruh lapangan, dikelompokkan menurut namanya.
 *
 * Laporan cutoff menjumlahkan per nama zona, jadi nama adalah kuncinya dan
 * bukan sekadar label: satu huruf yang berbeda memecah satu baris ringkasan
 * menjadi dua yang tidak akan pernah dijumlahkan bersama. Karena itu daftar
 * di kiri ini adalah daftar baris laporan yang akan keluar nanti — dan sebuah
 * nama yang muncul dua kali dengan ejaan berbeda terlihat di sini sebelum ia
 * terlihat di dalam laporan.
 */
function ZonesTab({
  project,
  wells,
  mayWrite,
  onChanged,
}: {
  project: Project;
  wells: Well[];
  mayWrite: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [scope, setScope] = useState<Scope>("all");
  const [wellQuery, setWellQuery] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [applying, setApplying] = useState<{ name: string } | null>(null);
  const [doomed, setDoomed] = useState<{ what: string; ids: number[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const groups = useMemo<ZoneGroup[]>(() => {
    const map = new Map<string, ZoneGroup>();
    for (const well of wells)
      for (const zone of well.zones) {
        const group = map.get(zone.name) ?? { name: zone.name, color: zone.color, rows: [], mean: 0 };
        group.rows.push({ zone, well });
        map.set(zone.name, group);
      }
    const list = [...map.values()];
    for (const group of list)
      group.mean = group.rows.reduce((sum, row) => sum + row.zone.top_md, 0) / group.rows.length;
    return list.sort((a, b) => a.mean - b.mean);
  }, [wells]);

  const group = groups.find((row) => row.name === chosen) ?? groups[0] ?? null;

  const rows = useMemo(
    () =>
      group
        ? wells.map((well) => ({
            id: well.id,
            well,
            zone: well.zones.find((zone) => zone.name === group.name) ?? null,
          }))
        : [],
    [wells, group],
  );

  const shown = useMemo(
    () => narrow(rows, (row) => row.zone !== null, scope, wellQuery),
    [rows, scope, wellQuery],
  );

  const pickedRows = shown.filter((row) => picked.has(row.id));
  const write = async (label: string, work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      toast(label);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: "well",
      label: "Well",
      render: (row) => (
        <span style={{ font: "500 11px var(--font-sans)", color: row.zone ? "var(--ink)" : "var(--ink-6)" }}>
          {row.well.name}
        </span>
      ),
    },
    {
      key: "top",
      label: "Puncak (m)",
      width: 96,
      align: "right",
      render: (row) =>
        row.zone ? (
          <NumberCell
            value={row.zone.top_md}
            disabled={!mayWrite}
            label={`Top of ${row.zone.name} on ${row.well.name}`}
            onCommit={(top_md) =>
              write(`Top of ${row.zone!.name} on ${row.well.name} → ${top_md.toFixed(1)} m.`, () =>
                api.patch(`/api/zones/${row.zone!.id}/`, { top_md }),
              )
            }
          />
        ) : (
          <span style={{ color: "var(--ink-7)" }}>—</span>
        ),
    },
    {
      key: "base",
      label: "Alas (m)",
      width: 96,
      align: "right",
      render: (row) =>
        row.zone ? (
          <NumberCell
            value={row.zone.base_md}
            disabled={!mayWrite}
            label={`Base of ${row.zone.name} on ${row.well.name}`}
            onCommit={(base_md) =>
              write(`Base of ${row.zone!.name} on ${row.well.name} → ${base_md.toFixed(1)} m.`, () =>
                api.patch(`/api/zones/${row.zone!.id}/`, { base_md }),
              )
            }
          />
        ) : (
          <span style={{ color: "var(--ink-7)" }}>—</span>
        ),
    },
    {
      key: "gross",
      label: "Thickness",
      width: 76,
      align: "right",
      render: (row) =>
        row.zone ? `${(row.zone.base_md - row.zone.top_md).toFixed(1)} m` : (
          <span style={{ color: "var(--ink-7)" }}>—</span>
        ),
    },
  ];

  return (
    <div style={{ display: "flex", gap: 9, flex: 1, minHeight: 0 }}>
      <NameList
        rows={groups.map((row) => ({
          key: row.name,
          label: row.name,
          color: row.color,
          meta: `${row.rows.length}/${wells.length}`,
        }))}
        selected={group?.name ?? null}
        onSelect={(key) => {
          setChosen(key);
          setPicked(new Set());
        }}
        query={query}
        onQuery={setQuery}
        placeholder="Search zone name…"
        empty="This field has no zone yet."
        action={
          mayWrite ? (
            <Button
              variant="ghost"
              title="Set a new zone on many wells at once"
              onClick={() => setApplying({ name: "" })}
              style={{ height: 24, font: "500 10px var(--font-sans)" }}
            >
              {Icon.plus({ size: 10 })} Baru
            </Button>
          ) : undefined
        }
      />

      {group === null ? (
        <Detail bar={<span className="label">Zones</span>}>
          <Empty>
            No zone in this field yet. Zones are what the cutoff report sums over — make the
            first one with the New button, preferably from a pair of markers.
          </Empty>
        </Detail>
      ) : (
        <Detail
          bar={
            <>
              <span style={{ font: "600 11.5px var(--font-sans)", color: "var(--ink)" }}>
                {group.name}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {group.rows.length} dari {wells.length} sumur · tebal rata-rata{" "}
                {(
                  group.rows.reduce((sum, row) => sum + (row.zone.base_md - row.zone.top_md), 0) /
                  group.rows.length
                ).toFixed(1)}{" "}
                m
              </span>
              <span style={{ flex: 1 }} />
              {mayWrite && (
                <>
                  <ColorPicker
                    value={group.color}
                    compact
                    label={`Colour of ${group.name}`}
                    onChange={(color) =>
                      write(`Colour of ${group.name} changed on ${group.rows.length} wells.`, async () => {
                        const failed = await patchEach(
                          group.rows.map((row) => `/api/zones/${row.zone.id}/`),
                          { color },
                        );
                        if (failed) throw new Error(`${failed} sumur gagal diubah.`);
                      })
                    }
                  />
                  <Button variant="ghost" onClick={() => setRenaming(true)} style={{ height: 22 }}>
                    Rename
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setApplying({ name: group.name })}
                    style={{ height: 22 }}
                  >
                    Set on wells…
                  </Button>
                  <Button
                    variant="danger"
                    style={{ height: 22 }}
                    onClick={() => {
                      // Seperti pada marker: tanpa centang, yang terhapus
                      // adalah apa yang sedang terlihat, bukan seluruh
                      // lapangan yang sedang tersembunyi di balik penyaring.
                      const targets = (pickedRows.length ? pickedRows : shown).filter(
                        (row) => row.zone,
                      );
                      if (!targets.length) {
                        toast("No zone on the wells that were picked.", "error");
                        return;
                      }
                      setDoomed({
                        what:
                          targets.length === group.rows.length
                            ? `Zone ${group.name} on all ${targets.length} wells`
                            : `Zone ${group.name} on ${targets.length} picked wells`,
                        ids: targets.map((row) => row.zone!.id),
                      });
                    }}
                  >
                    {Icon.trash({ size: 10 })} Hapus
                  </Button>
                </>
              )}
            </>
          }
          filter={
            <RowFilter
              scope={scope}
              onScope={setScope}
              query={wellQuery}
              onQuery={setWellQuery}
              counts={{
                all: wells.length,
                has: group.rows.length,
                missing: wells.length - group.rows.length,
              }}
            />
          }
        >
          <Table
            rows={shown}
            columns={columns}
            selected={picked}
            onSelected={mayWrite ? setPicked : undefined}
            selectLabel={(row) => `Select well ${row.well.name}`}
            empty={
              wells.length === 0
                ? "This field has no well yet."
                : "No well matches this filter."
            }
          />
        </Detail>
      )}

      {renaming && group && (
        <RenameDialog
          title="Rename zone"
          what={`${group.name} is set on ${group.rows.length} wells. The name changes on all of them at once.`}
          initial={group.name}
          busy={busy}
          note={
            <Notice tone="info">
              The cutoff report sums by name, so renaming on only some of the wells splits one
              report row into two. That is why this rename always touches the whole field.
            </Notice>
          }
          onClose={() => setRenaming(false)}
          onSubmit={(name) =>
            write(`Zone ${group.name} renamed to ${name}.`, async () => {
              await api.post("/api/zones/rename/", {
                project: project.id,
                from: group.name,
                to: name,
              });
              setRenaming(false);
              setChosen(name);
            })
          }
        />
      )}

      {applying && (
        <ZoneApplyDialog
          project={project}
          wells={wells}
          name={applying.name}
          preset={picked}
          onClose={() => setApplying(null)}
          onDone={(name, message) => {
            setApplying(null);
            setChosen(name);
            toast(message);
            onChanged();
          }}
        />
      )}

      {doomed && (
        <DeleteDialog
          title="Delete zone"
          what={doomed.what}
          consequences={doomed.ids.length > 1 ? ["its row in the cutoff summary of every well"] : []}
          confirmWord={doomed.ids.length > 4 ? "DELETE" : undefined}
          busy={busy}
          onCancel={() => setDoomed(null)}
          onConfirm={() =>
            write(`${doomed.ids.length} zones deleted.`, async () => {
              await api.post("/api/zones/bulk-delete/", { ids: doomed.ids });
              setDoomed(null);
              setPicked(new Set());
            })
          }
        />
      )}
    </div>
  );
}

/**
 * Memasang satu zona di banyak sumur sekaligus.
 *
 * Dua cara, dan yang pertama hampir selalu yang benar. Sebuah lapisan yang
 * ditemui pada 2.100 m di satu sumur ada pada 2.340 m di sumur seberangnya;
 * menyalin sepasang angka ke seluruh lapangan menghasilkan dua puluh zona yang
 * tak satu pun menunjuk batuan yang dimaksudkan. Sepasang marker memulangkan
 * kedalaman yang berbeda-beda itu dari tiap sumur sendiri.
 */
function ZoneApplyDialog({
  project,
  wells,
  name: initial,
  preset,
  onClose,
  onDone,
}: {
  project: Project;
  wells: Well[];
  name: string;
  preset: Set<number>;
  onClose: () => void;
  onDone: (name: string, message: string) => void;
}) {
  const markerNames = useMemo(() => {
    const depths = new Map<string, number[]>();
    for (const well of wells)
      for (const marker of well.markers)
        depths.set(marker.name, [...(depths.get(marker.name) ?? []), marker.md]);
    return [...depths.entries()]
      .map(([name, list]) => ({
        value: name,
        label: name,
        hint: `${list.length} wells`,
        mean: list.reduce((sum, md) => sum + md, 0) / list.length,
      }))
      .sort((a, b) => a.mean - b.mean);
  }, [wells]);

  const [name, setName] = useState(initial);
  const [mode, setMode] = useState<"marker" | "depth">(markerNames.length >= 2 ? "marker" : "depth");
  const [topMarker, setTopMarker] = useState(markerNames[0]?.value ?? "");
  const [baseMarker, setBaseMarker] = useState(markerNames[1]?.value ?? "");
  const [top, setTop] = useState("");
  const [base, setBase] = useState("");
  const [color, setColor] = useState(
    wells.flatMap((well) => well.zones).find((zone) => zone.name === initial)?.color ?? PALETTE[0],
  );
  const [picked, setPicked] = useState<Set<number>>(() =>
    preset.size
      ? new Set(preset)
      : new Set(wells.filter((well) => !well.zones.some((z) => z.name === initial)).map((w) => w.id)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const has = (well: Well) => well.zones.some((zone) => zone.name === name);

  const submit = async () => {
    if (!name.trim()) {
      setError("A zone must have a name.");
      return;
    }
    if (picked.size === 0) {
      setError("Pick at least one well.");
      return;
    }
    const body: Record<string, unknown> = {
      project: project.id,
      name: name.trim(),
      color,
      wells: [...picked],
    };
    if (mode === "marker") {
      if (!topMarker || !baseMarker || topMarker === baseMarker) {
        setError("Pick two different markers.");
        return;
      }
      body.top_marker = topMarker;
      body.base_marker = baseMarker;
    } else {
      const a = Number(top);
      const b = Number(base);
      if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) {
        setError("The base must be deeper than the top.");
        return;
      }
      body.top_md = a;
      body.base_md = b;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{
        created: number[];
        updated: number[];
        skipped: { well: string; reason: string }[];
      }>("/api/zones/apply-to-wells/", body);
      const skipped = result.skipped.length
        ? `, ${result.skipped.length} dilewati (${result.skipped
            .slice(0, 3)
            .map((row) => row.well)
            .join(", ")}${result.skipped.length > 3 ? "…" : ""})`
        : "";
      onDone(
        name.trim(),
        `${name.trim()}: ${result.created.length} dibuat, ${result.updated.length} diperbarui${skipped}.`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={initial ? `Set zone ${initial}` : "New zone on many wells"}
      width={490}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={submit}>
            Pasang
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <TextField label="ZONE NAME" value={name} onChange={setName} autoFocus={!initial} />
        </div>
        <ColorPicker value={color} onChange={setColor} label="Colour" />
      </div>

      <Segmented
        value={mode}
        options={[
          { value: "marker", label: "From a pair of markers" },
          { value: "depth", label: "Fixed depths" },
        ]}
        onChange={setMode}
      />

      {mode === "marker" ? (
        markerNames.length >= 2 ? (
          <>
            <div style={{ display: "flex", gap: 10 }}>
              <Dropdown
                label="PUNCAK"
                value={topMarker}
                options={markerNames}
                onChange={setTopMarker}
                width={168}
              />
              <Dropdown
                label="ALAS"
                value={baseMarker}
                options={markerNames}
                onChange={setBaseMarker}
                width={168}
              />
            </div>
            <Notice tone="info">
              Each well uses the depth of its own markers. A well missing one of the two is
              skipped and named — rather than given a guessed zone that a week later reads as
              somebody's work.
            </Notice>
          </>
        ) : (
          <Notice tone="warn">
            This field has fewer than two marker names. Set the markers first in the Marker tab,
            or use fixed depths.
          </Notice>
        )
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <TextField label="TOP (M MD)" value={top} onChange={setTop} />
            <TextField label="BASE (M MD)" value={base} onChange={setBase} />
          </div>
          <Notice tone="warn">
            One pair of numbers for every well is right only if the bed really is flat. Where the
            markers exist, the choice on the left is far closer to the truth.
          </Notice>
        </>
      )}

      <WellPicker wells={wells} has={has} picked={picked} onPicked={setPicked} />
      {error && <Notice tone="error">{error}</Notice>}
    </Modal>
  );
}

/* -------------------------------------------------------------- Set kurva */

const KIND_LABEL: Record<string, string> = {
  raw: "RAW",
  interpretation: "INTERPRETASI",
  core: "CORE",
  multimin: "MULTI-MINERAL",
};

interface SetGroup {
  key: string;
  name: string;
  kind: string;
  rows: { set: CurveSetBrief; well: Well }[];
}

/**
 * Set kurva seluruh lapangan, dikelompokkan menurut nama dan jenisnya.
 *
 * Tiap perhitungan menerbitkan versi baru dan menyimpan yang lama — itulah
 * yang membuat interpretasi tahun lalu masih bisa direproduksi, dan itu benar.
 * Yang tidak benar adalah tidak adanya satu tempat pun untuk melihat akibatnya:
 * sesudah sepuluh kali hitung ulang di enam puluh sumur, enam ratus set lama
 * duduk di basis data dan satu-satunya cara melihatnya adalah membuka pohon
 * proyek sumur demi sumur.
 *
 * "Pilih versi lama" karena itu adalah tombol yang paling banyak dipakai di
 * tab ini: ia menandai semua yang bukan versi aktif, dan tidak satu pun yang
 * aktif — sebab pembersihan yang bisa menghapus kurva yang sedang tergambar
 * di layar bukan pembersihan melainkan kecelakaan.
 */
function SetsTab({
  wells,
  mayWrite,
  onChanged,
}: {
  wells: Well[];
  mayWrite: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  /* Tab ini membuka pada set yang sudah ada, bukan pada seluruh sumur —
     satu-satunya tab yang begitu. Yang dikerjakan orang di sini adalah
     versinya: mengaktifkan yang benar, membuang yang lama, dan tak satu pun
     dari keduanya berlaku atas sumur yang belum punya set sama sekali. Pada
     lapangan berisi 1.402 sumur, membuka pada "semua" berarti 1.397 baris
     kosong lebih dulu, dan delapan set yang dicari terkubur di bawahnya.
     Sumur yang belum punya tetap satu klik jauhnya, di "Belum". */
  const [scope, setScope] = useState<Scope>("has");
  const [wellQuery, setWellQuery] = useState("");
  const [doomed, setDoomed] = useState<{ what: string; ids: number[]; curves: number } | null>(null);
  const [busy, setBusy] = useState(false);

  const groups = useMemo<SetGroup[]>(() => {
    const map = new Map<string, SetGroup>();
    for (const well of wells)
      for (const set of well.curve_sets) {
        const key = `${set.kind}|${set.name}`;
        const group = map.get(key) ?? { key, name: set.name, kind: set.kind, rows: [] };
        group.rows.push({ set, well });
        map.set(key, group);
      }
    return [...map.values()].sort(
      (a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name),
    );
  }, [wells]);

  const group = groups.find((row) => row.key === chosen) ?? groups[0] ?? null;

  /**
   * Tiap versi satu baris, dan tiap sumur yang *belum* punya set bernama ini
   * juga satu baris.
   *
   * "Sumur mana yang belum dihitung" adalah pertanyaan yang sama bentuknya
   * dengan "sumur mana yang belum punya Top Menggala", dan jawabannya tidak
   * ada di dalam daftar yang hanya memuat set yang sudah terbit. Baris kosong
   * memakai id negatif: ia harus tidak mungkin bertabrakan dengan id set mana
   * pun, sebab keduanya duduk dalam satu himpunan pilihan.
   */
  const rows = useMemo(() => {
    if (!group) return [];
    const owns = new Set(group.rows.map((row) => row.well.id));
    const list: { id: number; set: CurveSetBrief | null; well: Well }[] = [
      ...group.rows.map((row) => ({ id: row.set.id, set: row.set as CurveSetBrief | null, well: row.well })),
      ...wells
        .filter((well) => !owns.has(well.id))
        .map((well) => ({ id: -well.id, set: null, well })),
    ];
    return list.sort(
      (a, b) =>
        a.well.name.localeCompare(b.well.name) || (b.set?.version ?? 0) - (a.set?.version ?? 0),
    );
  }, [group, wells]);

  const shown = useMemo(
    () => narrow(rows, (row) => row.set !== null, scope, wellQuery),
    [rows, scope, wellQuery],
  );

  /** Seluruh set kelompok ini — yang dihitung bilah rincian di atas tabel. */
  const published = rows.filter((row) => row.set);
  const staleAll = published.filter((row) => !row.set!.is_active);
  /* Kedua ini dihitung dari yang *terlihat*, bukan dari seluruh kelompok:
     tombol yang menandai — lalu menghapus — baris yang sedang tersembunyi di
     balik penyaring adalah tombol yang bekerja di luar pandangan orang yang
     menekannya. Baris sumur yang kosong tidak ikut, meski "pilih semua"
     menandainya: ia bukan sesuatu yang bisa dihapus. */
  const stale = shown.filter((row) => row.set && !row.set.is_active);
  const pickedSets = shown.filter((row) => row.set && picked.has(row.id));
  /** Berapa sumur yang punya set bernama ini — bukan berapa versinya. */
  const owned = group ? new Set(group.rows.map((row) => row.well.id)).size : 0;

  const write = async (label: string, work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      toast(label);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: "well",
      label: "Well",
      render: (row) => (
        <span style={{ font: "500 11px var(--font-sans)", color: row.set ? "var(--ink)" : "var(--ink-6)" }}>
          {row.well.name}
        </span>
      ),
    },
    {
      key: "version",
      label: "Version",
      width: 62,
      align: "right",
      render: (row) =>
        row.set ? `v${row.set.version}` : <span style={{ color: "var(--ink-7)" }}>—</span>,
    },
    {
      key: "curves",
      label: "Curves",
      width: 62,
      align: "right",
      render: (row) =>
        row.set ? row.set.curve_count : <span style={{ color: "var(--ink-7)" }}>—</span>,
    },
    {
      key: "mnemonics",
      label: "Contents",
      render: (row) =>
        row.set ? (
          <span title={row.set.mnemonics.join(", ")} style={{ color: "var(--ink-4)" }}>
            {row.set.mnemonics.slice(0, 6).join(" ")}
            {row.set.mnemonics.length > 6 ? " …" : ""}
          </span>
        ) : (
          <span style={{ color: "var(--ink-7)" }}>carries no set under this name</span>
        ),
    },
    {
      key: "state",
      label: "Keadaan",
      width: 96,
      render: (row) =>
        !row.set ? (
          <span style={{ color: "var(--ink-7)" }}>—</span>
        ) : row.set.is_active ? (
          <span style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--accent)" }}>
            {Icon.check({ size: 10 })} aktif
          </span>
        ) : (
          <span style={{ color: "var(--ink-6)" }}>older</span>
        ),
    },
    {
      key: "act",
      label: "",
      width: 76,
      render: (row) =>
        row.set && !row.set.is_active && mayWrite ? (
          <Button
            variant="ghost"
            style={{ height: 20, font: "500 10px var(--font-sans)" }}
            title={`Make ${row.set.name} v${row.set.version} the active set on ${row.well.name}`}
            onClick={() =>
              write(`${row.set!.name} v${row.set!.version} is now active on ${row.well.name}.`, () =>
                api.post(`/api/curve-sets/${row.set!.id}/activate/`),
              )
            }
          >
            Activate
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: "flex", gap: 9, flex: 1, minHeight: 0 }}>
      <NameList
        rows={groups.map((row) => ({
          key: row.key,
          label: row.name,
          meta: `${new Set(row.rows.map((one) => one.well.id)).size}/${wells.length}`,
        }))}
        selected={group?.key ?? null}
        onSelect={(key) => {
          setChosen(key);
          setPicked(new Set());
        }}
        query={query}
        onQuery={setQuery}
        placeholder="Search set name…"
        empty="This field has no curve set yet."
      />

      {group === null ? (
        <Detail bar={<span className="label">Curve sets</span>}>
          <Empty>No curve in this field yet. Import a LAS to make its first set.</Empty>
        </Detail>
      ) : (
        <Detail
          bar={
            <>
              <span style={{ font: "600 11.5px var(--font-sans)", color: "var(--ink)" }}>
                {group.name}
              </span>
              <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                {KIND_LABEL[group.kind] ?? group.kind}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {owned} dari {wells.length} sumur · {published.length} set · {staleAll.length}{" "}
                versi lama ·{" "}
                {published.reduce((sum, row) => sum + row.set!.curve_count, 0)} kurva
              </span>
              <span style={{ flex: 1 }} />
              {mayWrite && (
                <>
                  <Button
                    variant="ghost"
                    disabled={stale.length === 0}
                    style={{ height: 22 }}
                    title="Marks every version that is not its well's active one"
                    onClick={() => setPicked(new Set(stale.map((row) => row.id)))}
                  >
                    Pilih versi lama ({stale.length})
                  </Button>
                  <Button
                    variant="danger"
                    disabled={pickedSets.length === 0}
                    style={{ height: 22 }}
                    onClick={() => {
                      const targets = pickedSets;
                      const live = targets.filter((row) => row.set!.is_active);
                      setDoomed({
                        what:
                          targets.length === 1
                            ? `${targets[0].set!.name} v${targets[0].set!.version} — ${targets[0].well.name}`
                            : `${targets.length} curve sets named ${group.name}` +
                              (live.length ? ` (${live.length} of them still active)` : ""),
                        ids: targets.map((row) => row.set!.id),
                        curves: targets.reduce((sum, row) => sum + row.set!.curve_count, 0),
                      });
                    }}
                  >
                    {Icon.trash({ size: 10 })} Hapus ({pickedSets.length})
                  </Button>
                </>
              )}
            </>
          }
          filter={
            <RowFilter
              scope={scope}
              onScope={setScope}
              query={wellQuery}
              onQuery={setWellQuery}
              counts={{
                all: rows.length,
                has: published.length,
                missing: rows.length - published.length,
              }}
            />
          }
        >
          <Table
            rows={shown}
            columns={columns}
            selected={picked}
            onSelected={mayWrite ? setPicked : undefined}
            selectLabel={(row) =>
              row.set
                ? `Select ${row.set.name} v${row.set.version} on ${row.well.name}`
                : `Well ${row.well.name} has no such set yet`
            }
            empty="No well matches this filter."
          />
        </Detail>
      )}

      {doomed && (
        <DeleteDialog
          title="Delete curve set"
          what={doomed.what}
          consequences={[`the ${doomed.curves} curves inside them`]}
          confirmWord={doomed.ids.length > 4 ? "DELETE" : undefined}
          busy={busy}
          onCancel={() => setDoomed(null)}
          onConfirm={() =>
            write(`${doomed.ids.length} curve sets deleted along with their ${doomed.curves} curves.`, async () => {
              await api.post("/api/curve-sets/bulk-delete/", { ids: doomed.ids });
              setDoomed(null);
              setPicked(new Set());
            })
          }
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Log */

/** Satu log sebuah sumur, sebagaimana dipulangkan `/log-inventory/`. */
interface LogRow {
  id: number;
  well: number;
  well_name: string;
  set: string;
  version: number;
  unit: string;
  is_active: boolean;
  off_reason: string;
  coverage: number | null;
}

interface LogInventory {
  mnemonics: { mnemonic: string; on: number; off: number; wells: number }[];
  mnemonic: string;
  rows: LogRow[];
  wells: number;
}

/**
 * Log mana yang dipakai di lapangan ini, dan di sumur mana.
 *
 * Tab ini ada karena keputusannya hampir tidak pernah tentang satu kurva.
 * "Densitas di lapangan ini menempel dinding di tiap sumur yang dilog sebelum
 * 1998" adalah satu penilaian atas ratusan baris, dan pohon proyek — tempat
 * sakelar satuannya duduk — hanya memajang satu sumur pada satu saat. Di
 * Prabumulih dengan 1.451 sumurnya, mematikan satu mnemonik selapangan lewat
 * pohon itu adalah ribuan klik yang tak seorang pun akan menyelesaikannya, dan
 * yang terjadi bukan pekerjaan yang tertunda melainkan penilaian yang tidak
 * pernah diberlakukan sama sekali.
 *
 * Yang dipajang di kiri adalah **nama log**, bukan sumur, dan itu urutan
 * pertanyaannya: yang dinilai adalah alatnya. Di kanan, satu baris per sumur
 * yang membawanya — dengan cakupannya, sebab itulah sebab yang paling sering
 * ada di belakang sebuah log yang dimatikan.
 */
function LogsTab({
  project,
  mayWrite,
  onChanged,
}: {
  project: Project;
  mayWrite: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  /**
   * Naik setiap kali sebuah sakelar tersimpan, supaya muatannya dibaca ulang.
   *
   * Ada di dalam kunci `useAsync` sebab keadaan itu hidup di server: tanpa ini,
   * mematikan delapan log memulangkan tabel yang sama persis dari singgahan,
   * dan yang terlihat adalah delapan baris yang tetap berkata "in use" sesudah
   * pesan yang mengatakan keduanya sudah dimatikan.
   */
  const [saved, setSaved] = useState(0);

  const inventory = useAsync(
    (signal) =>
      api.get<LogInventory>(
        `/api/projects/${project.id}/log-inventory/`,
        chosen ? { mnemonic: chosen } : {},
        signal,
      ),
    [project.id, chosen, saved],
  );

  const data = inventory.data;
  const names = data?.mnemonics ?? [];
  // Yang terpilih dijaga tetap ada di dalam daftarnya. Sebuah mnemonik yang
  // lenyap — kurvanya dihapus di layar lain — meninggalkan tabel yang memajang
  // baris milik nama yang sudah tidak disebut di kiri.
  const current = chosen && names.some((row) => row.mnemonic === chosen) ? chosen : null;
  // Barisnya sudah disaring server menurut mnemonik yang dibuka, dan cacahnya
  // paling banyak sebanyak sumur lapangan ini — jadi tidak ada penyaring kedua
  // di sini. Kotak pencarian di kiri menyaring **nama log**, yaitu daftar yang
  // memang bisa panjang.
  const shown = current ? (data?.rows ?? []) : [];

  const pickedRows = shown.filter((row) => picked.has(row.id));
  const on = shown.filter((row) => row.is_active);
  const off = shown.filter((row) => !row.is_active);

  const switchThem = async (ids: number[], isActive: boolean) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const body: Record<string, unknown> = { ids, is_active: isActive };
      if (!isActive && reason.trim()) body.off_reason = reason.trim();
      const answer = await api.post<{ moved: number[]; matched: number[] }>(
        "/api/curves/set-active/", body);
      const moved = answer.moved.length;
      toast(
        moved === 0
          ? "Every one of those was already that way — nothing changed."
          : isActive
            ? `${moved} log${moved > 1 ? "s are" : " is"} used again.`
            : `${moved} log${moved > 1 ? "s are" : " is"} no longer used — nothing is computed or drawn from ${moved > 1 ? "them" : "it"}.`,
      );
      setPicked(new Set());
      setSaved((v) => v + 1);
      // Layar di belakangnya ikut: `curve_sets` dan tiap pemilih kurva di sana
      // dibangun dari muatan yang tidak ikut dimuat ulang oleh tab ini.
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<LogRow>[] = [
    {
      key: "well",
      label: "Well",
      render: (row) => (
        <span style={{
          font: "500 11px var(--font-sans)",
          color: row.is_active ? "var(--ink)" : "var(--ink-6)",
        }}>
          {row.well_name}
        </span>
      ),
    },
    {
      key: "set",
      label: "Set",
      render: (row) => (
        <span style={{ color: "var(--ink-4)" }}>{row.set} v{row.version}</span>
      ),
    },
    {
      key: "unit",
      label: "Unit",
      width: 64,
      render: (row) => row.unit || <span style={{ color: "var(--ink-7)" }}>—</span>,
    },
    {
      key: "coverage",
      label: "Filled",
      width: 64,
      align: "right",
      // Cakupannya dipajang sebab ia sebab yang paling sering ada di belakang
      // sebuah log yang dimatikan — dan sebuah kolom yang menyebutkannya
      // membuat keputusan itu bisa diambil di sini, tanpa membuka tiap sumur.
      render: (row) =>
        row.coverage === null
          ? <span style={{ color: "var(--ink-7)" }}>—</span>
          : <span style={{ color: row.coverage < 0.5 ? "var(--amber)" : "var(--ink-3)" }}>
              {Math.round(row.coverage * 100)}%
            </span>,
    },
    {
      key: "state",
      label: "Keadaan",
      width: 150,
      render: (row) =>
        row.is_active ? (
          <span style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--accent)" }}>
            {Icon.check({ size: 10 })} in use
          </span>
        ) : (
          <span title={row.off_reason} style={{ color: "var(--ink-6)" }}>
            off{row.off_reason ? ` · ${row.off_reason}` : ""}
          </span>
        ),
    },
  ];

  return (
    <div style={{ display: "flex", gap: 9, flex: 1, minHeight: 0 }}>
      <NameList
        rows={names.map((row) => ({
          key: row.mnemonic,
          label: row.mnemonic,
          // "3 off" disebut di daftar kiri, bukan hanya di dalam tabelnya: itu
          // satu-satunya cara melihat di mana ada log yang dimatikan tanpa
          // membuka tiap nama satu per satu.
          meta: `${row.wells}${row.off ? ` · ${row.off} off` : ""}`,
        }))}
        selected={current}
        onSelect={(key) => {
          setChosen(key);
          setPicked(new Set());
          setReason("");
        }}
        query={query}
        onQuery={setQuery}
        placeholder="Search log name…"
        empty="This field has no curve yet."
      />

      {current === null ? (
        <Detail bar={<span className="label">Logs</span>}>
          {inventory.loading ? (
            <Loading label="Reading which logs this field carries…" />
          ) : (
            <Empty>
              Pick a log on the left. Switching one off keeps it stored and
              leaves it out of every calculation and every display.
            </Empty>
          )}
        </Detail>
      ) : (
        <Detail
          bar={
            <>
              <span style={{ font: "600 11.5px var(--font-mono)", color: "var(--ink)" }}>
                {current}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {shown.length} dari {data?.wells ?? 0} sumur · {on.length} in use
                {off.length ? ` · ${off.length} off` : ""}
              </span>
              <span style={{ flex: 1 }} />
              {mayWrite && (
                <>
                  <Button
                    variant="ghost"
                    disabled={busy || on.length === 0}
                    style={{ height: 22 }}
                    title={`Mark every well where ${current} is still in use`}
                    onClick={() => setPicked(new Set(on.map((row) => row.id)))}
                  >
                    Pilih yang terpakai ({on.length})
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy || pickedRows.length === 0}
                    style={{ height: 22 }}
                    onClick={() => switchThem(pickedRows.map((row) => row.id), true)}
                  >
                    {Icon.power({ size: 10 })} Use ({pickedRows.length})
                  </Button>
                  <Button
                    variant="danger"
                    disabled={busy || pickedRows.length === 0}
                    style={{ height: 22 }}
                    title={`Stop using ${current} in the wells marked — the curves stay stored`}
                    onClick={() => switchThem(pickedRows.map((row) => row.id), false)}
                  >
                    {Icon.power({ size: 10 })} Stop using ({pickedRows.length})
                  </Button>
                </>
              )}
            </>
          }
          filter={
            mayWrite ? (
              // Sebabnya boleh kosong dan tetap ditawarkan lebih dulu. Sebuah
              // log yang mati tanpa sebab adalah pertanyaan yang tertinggal bagi
              // tiap orang berikutnya yang membuka sumur itu — rusak, atau
              // seseorang lupa menyalakannya lagi? — dan satu kalimat di sini
              // menjawabnya tanpa perlu mencari orangnya.
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    label="Reason"
                    value={reason}
                    onChange={setReason}
                    placeholder={`Why ${current} is not used — sticking, never normalised, second run…`}
                  />
                </div>
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)",
                               whiteSpace: "nowrap", paddingBottom: 6 }}>
                  kept with the logs you switch off
                </span>
              </div>
            ) : undefined
          }
        >
          <Table
            rows={shown}
            columns={columns}
            selected={picked}
            onSelected={mayWrite ? setPicked : undefined}
            selectLabel={(row) =>
              row.is_active
                ? `Select ${current} on ${row.well_name}, in use`
                : `Select ${current} on ${row.well_name}, switched off`
            }
            empty="No well carries this log."
          />
        </Detail>
      )}
    </div>
  );
}
